import type {
  MultipleChoiceGroup,
  TemplateNode,
  TemplatePage,
} from '@ulysses-inc/harami_api_client'
import type { ChoiceIdMapping } from './types'
import { flattenNodes } from './uuidMapping'

export const collectReferencedTemplateSpecificGroups = (
  pages: TemplatePage[],
  sourceGroups: MultipleChoiceGroup[],
): MultipleChoiceGroup[] => {
  const sourceGroupById = new Map<number, MultipleChoiceGroup>()
  const sourceGroupByChoiceId = new Map<number, MultipleChoiceGroup>()
  for (const group of sourceGroups) {
    if (group.id !== undefined) sourceGroupById.set(group.id, group)
    for (const choice of group.responses) {
      if (choice.id !== undefined) sourceGroupByChoiceId.set(choice.id, group)
    }
  }

  const referencedGroups = new Set<MultipleChoiceGroup>()
  for (const node of flattenNodes(pages)) {
    for (const group of node.question?.responseMultipleChoices ?? []) {
      if (group.id === undefined) continue
      const sourceGroup = sourceGroupById.get(group.id)
      if (sourceGroup) referencedGroups.add(sourceGroup)
    }
    for (const choice of node.logic?.responseMultipleChoices ?? []) {
      if (choice.id === undefined) continue
      const sourceGroup = sourceGroupByChoiceId.get(choice.id)
      if (sourceGroup) referencedGroups.add(sourceGroup)
    }
  }

  return sourceGroups.filter(group => referencedGroups.has(group))
}

export const createChoiceIdMapping = (
  sourceGroups: MultipleChoiceGroup[],
  targetGroups: MultipleChoiceGroup[],
): ChoiceIdMapping => {
  const mapping: ChoiceIdMapping = {
    groupIds: new Map(),
    choiceIds: new Map(),
  }

  // ひな形限定選択肢は送った順番で作成結果が返るため、
  // 作成前後のIDを同じ順番で対応づける。
  sourceGroups.forEach((sourceGroup, groupIndex) => {
    const targetGroup = targetGroups[groupIndex]!
    mapping.groupIds.set(sourceGroup.id!, targetGroup.id!)

    sourceGroup.responses.forEach((sourceChoice, choiceIndex) => {
      mapping.choiceIds.set(
        sourceChoice.id!,
        targetGroup.responses[choiceIndex]!.id!,
      )
    })
  })

  return mapping
}

// 作り直したひな形限定選択肢のIDだけを書き換える。
// 通常の選択肢はレルム内で共通のため、元のIDをそのまま使う。
const replaceId = (
  id: number | undefined,
  mapping: Map<number, number>,
): number | undefined =>
  id === undefined ? undefined : (mapping.get(id) ?? id)

export const rewriteTemplateSpecificChoiceReferences = (
  pages: TemplatePage[],
  mapping: ChoiceIdMapping,
): TemplatePage[] => {
  const rewriteNode = (node: TemplateNode): TemplateNode => ({
    ...node,
    question: node.question
      ? {
          ...node.question,
          responseMultipleChoices: node.question.responseMultipleChoices?.map(
            group => ({
              ...group,
              id: replaceId(group.id, mapping.groupIds),
              responses: group.responses.map(choice => ({
                ...choice,
                id: replaceId(choice.id, mapping.choiceIds),
              })),
            }),
          ),
        }
      : undefined,
    logic: node.logic
      ? {
          ...node.logic,
          responseMultipleChoices: node.logic.responseMultipleChoices?.map(
            choice => ({
              ...choice,
              id: replaceId(choice.id, mapping.choiceIds),
            }),
          ),
        }
      : undefined,
    nodes: node.nodes?.map(rewriteNode),
  })

  return pages.map(page => ({ ...page, nodes: page.nodes?.map(rewriteNode) }))
}
